import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Bot,
  User,
  TrendingUp,
  Package,
  DollarSign,
  Target,
  RotateCcw,
  Copy,
  Check,
  Loader2,
  ChevronRight,
  Brain,
  BarChart3,
  ShoppingCart,
  Users,
  Database,
  Zap,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { getFirebaseFirestore } from '@/services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const db = getFirebaseFirestore();
const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

interface ToolCall {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isTyping?: boolean;
  toolCalls?: ToolCall[];      // AI aksiyon önerisi
  toolResult?: { success: boolean; message: string }; // Aksiyon sonucu
}

interface SuggestedQuestion {
  icon: React.ElementType;
  text: string;
  category: 'finance' | 'stock' | 'marketing' | 'competitor' | 'general';
  color: string;
  includePlatformData?: boolean;
}

const SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
  {
    icon: DollarSign,
    text: 'Bu ay ne kadar reklam harcamalıyım?',
    category: 'finance',
    color: 'green'
  },
  {
    icon: Package,
    text: 'Stok durumum nasıl, kritik ürünler var mı?',
    category: 'stock',
    color: 'orange',
    includePlatformData: true,
  },
  {
    icon: Target,
    text: 'Rakiplerime göre fiyatlarım nasıl?',
    category: 'competitor',
    color: 'purple'
  },
  {
    icon: TrendingUp,
    text: 'Satışlarımı artırmak için ne yapmalıyım?',
    category: 'general',
    color: 'blue',
    includePlatformData: true,
  },
  {
    icon: BarChart3,
    text: 'Hangi pazaryerinde daha iyi performans gösteriyorum?',
    category: 'general',
    color: 'cyan',
    includePlatformData: true,
  },
  {
    icon: Users,
    text: 'Müşteri segmentasyonum nasıl?',
    category: 'marketing',
    color: 'pink'
  }
];

const QUICK_ACTIONS = [
  { icon: TrendingUp, label: 'Satış Analizi', color: 'blue' },
  { icon: Package, label: 'Stok Raporu', color: 'orange' },
  { icon: DollarSign, label: 'Bütçe Önerisi', color: 'green' },
  { icon: Target, label: 'Rakip Analizi', color: 'purple' }
];


// Tool adı → Türkçe etiket
const TOOL_LABELS: Record<string, { label: string; icon: React.ElementType; color: string; danger: boolean }> = {
  updateTrendyolPrice: { label: 'Trendyol Fiyat Güncelle', icon: DollarSign, color: 'orange', danger: false },
  updateTrendyolStock: { label: 'Trendyol Stok Güncelle', icon: Package, color: 'blue', danger: false },
  pauseGoogleAdsCampaign: { label: 'Google Ads Kampanyasını Durdur', icon: AlertTriangle, color: 'red', danger: true },
  pauseMetaCampaign: { label: 'Meta Ads Kampanyasını Durdur', icon: AlertTriangle, color: 'red', danger: true },
};

const AIChatPage: React.FC = () => {
  const { user } = useAuth();
  const { hasAccess, setShowPaywall } = useSubscription();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const [executingToolId, setExecutingToolId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Konuşma geçmişini OpenAI formatında tut
  const conversationRef = useRef<Array<{ role: 'user' | 'assistant'; content: string }>>([]);

  // Auto-scroll to bottom — sadece mesaj container'ını scroll et, sayfayı değil
  useEffect(() => {
    if (shouldAutoScroll && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, shouldAutoScroll]);

  // Kişiselleştirilmiş karşılama - AI profil verisini çek
  useEffect(() => {
    if (messages.length > 0 || !user?.uid) return;

    const buildGreeting = async () => {
      let companyName = '';
      let sector = '';
      let monthlyRevenue = 0;

      try {
        const profileDoc = await getDoc(doc(db, 'users', user.uid, 'ai_profile', 'data'));
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          companyName = data.companyInfo?.companyName || '';
          sector = data.companyInfo?.sector || '';
          monthlyRevenue = data.financialInfo?.monthlyRevenue || 0;
        }
      } catch {
        // profil yoksa generic greeting
      }

      const personalizedLine = companyName
        ? `**${companyName}** verilerinize erişimim var.${sector ? ` Sektör: ${sector}.` : ''}${monthlyRevenue ? ` Aylık ciro hedefiniz: ${monthlyRevenue.toLocaleString('tr-TR')}₺.` : ''}`
        : 'Profil bilgilerinizi ve bağlı platform verilerinizi analiz edebilirim.';

      const greeting: Message = {
        id: 'greeting',
        role: 'assistant',
        content: `👋 **Merhaba!** Ben AI Asistanınızım.

${personalizedLine}

Trendyol, Hepsiburada, Google Analytics ve diğer bağlı platformlarınızdan gelen verileri okuyabiliyor, karşılaştırmalı analiz yapıp **size özel** öneriler sunabiliyorum.

**Aşağıdaki sorulardan birini seçin veya kendi sorunuzu yazın:**`,
        timestamp: new Date()
      };
      setMessages([greeting]);
    };

    buildGreeting();
  }, [user?.uid]);

  const handleSend = async (
    text?: string,
    _opts: { includePlatformData?: boolean } = {}
  ) => {
    const content = (text ?? inputValue).trim();
    if (!content || isLoading) return;

    // Frontend erişim kontrolü
    if (!hasAccess) {
      setShowPaywall(true);
      return;
    }

    setShouldAutoScroll(true);

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date()
    };

    conversationRef.current = [...conversationRef.current, { role: 'user', content }];
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    const typingMessage: Message = {
      id: `typing-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isTyping: true
    };
    setMessages(prev => [...prev, typingMessage]);

    try {
      // Aksiyon gerektiren bir istek mi? (fiyat, stok, kampanya, durdur vb.)
      const actionKeywords = ['fiyat', 'stok', 'güncelle', 'durdur', 'kampanya', 'değiştir', 'artır', 'azalt'];
      const wantsAction = actionKeywords.some(k => content.toLowerCase().includes(k));

      const res = await fetch(`${FUNCTIONS_URL}/callAI`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.uid || 'anonymous',
          useCase: 'chat',
          messages: conversationRef.current.slice(-10),
          includePlatformData: true, // Her zaman platform verisi gönder
          enableTools: wantsAction,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        // Deneme süresi bitmişse paywall aç
        if (err.code === 'TRIAL_EXPIRED') {
          setShowPaywall(true);
          setMessages(prev => prev.filter(m => !m.isTyping));
          return;
        }
        throw new Error(err.error || 'Sunucu hatası');
      }

      const data = await res.json();

      // AI bir tool çağırmak istedi — onay akışı başlat
      if (data.toolCalls && data.toolCalls.length > 0) {
        const toolCallMessage: Message = {
          id: `tool-${Date.now()}`,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          toolCalls: data.toolCalls,
        };
        conversationRef.current = [...conversationRef.current, {
          role: 'assistant',
          content: `[Aksiyon önerildi: ${data.toolCalls.map((t: ToolCall) => t.name).join(', ')}]`,
        }];
        setMessages(prev => [...prev.filter(m => !m.isTyping), toolCallMessage]);
        return;
      }

      const response: string = data.response || '';

      conversationRef.current = [...conversationRef.current, { role: 'assistant', content: response }];

      setMessages(prev => {
        const filtered = prev.filter(m => !m.isTyping);
        return [...filtered, {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response,
          timestamp: new Date()
        }];
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Bilinmeyen hata';
      setMessages(prev => {
        const filtered = prev.filter(m => !m.isTyping);
        return [...filtered, {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ **Hata:** ${msg}\n\nOpenAI API anahtarınızın yapılandırıldığından emin olun.`,
          timestamp: new Date()
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question: string, opts: { includePlatformData?: boolean } = {}) => {
    // Soruyu doğrudan gönder (platform verisi dahil edilerek)
    handleSend(question, opts);
  };

  const handleCopy = (id: string, content: string) => {
    navigator.clipboard.writeText(content.replace(/\*\*/g, '').replace(/[•📊💡⚠️✅🎯📦📈👥🏪💰🤖👋]/g, ''));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Tool onaylama / reddetme
  const handleExecuteTool = async (messageId: string, tool: ToolCall, approved: boolean) => {
    if (!approved) {
      // Reddet → AI'ya bildir
      const rejectContent = `Kullanıcı "${TOOL_LABELS[tool.name]?.label || tool.name}" aksiyonunu reddetti.`;
      conversationRef.current = [...conversationRef.current, { role: 'user', content: rejectContent }];
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, toolCalls: undefined, toolResult: { success: false, message: 'Aksiyon reddedildi.' } }
          : m
      ));
      return;
    }

    setExecutingToolId(messageId);
    try {
      const res = await fetch(`${FUNCTIONS_URL}/executePlatformTool`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.uid || 'anonymous',
          toolName: tool.name,
          params: tool.arguments,
        }),
      });
      const result = await res.json();

      // Sonucu mesaja yaz
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, toolCalls: undefined, toolResult: { success: result.success, message: result.message } }
          : m
      ));

      // Sonucu AI konuşmasına ekle (takip eden soru için)
      const resultContent = result.success
        ? `Aksiyon başarıyla tamamlandı: ${result.message}`
        : `Aksiyon başarısız: ${result.message}`;
      conversationRef.current = [...conversationRef.current, { role: 'user', content: resultContent }];
    } catch {
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, toolCalls: undefined, toolResult: { success: false, message: 'Bağlantı hatası, tekrar deneyin.' } }
          : m
      ));
    } finally {
      setExecutingToolId(null);
    }
  };

  const handleReset = () => {
    setMessages([]);
    setShouldAutoScroll(false); // Reset scroll behavior
    // Re-trigger greeting
    setTimeout(() => {
      const greeting: Message = {
        id: 'greeting',
        role: 'assistant',
        content: `👋 **Merhaba!** Ben AI Asistanınızım.

E-ticaret işletmenizi analiz edip size özel öneriler sunabilirim. Profil bilgilerinizi ve bağlantılarınızı kullanarak:

• 💰 Bütçe optimizasyonu
• 📦 Stok yönetimi
• 🎯 Rekabet analizi
• 📈 Büyüme stratejileri

konularında yardımcı olabilirim.

**Aşağıdaki sorulardan birini seçin veya kendi sorunuzu yazın:**`,
        timestamp: new Date()
      };
      setMessages([greeting]);
    }, 100);
  };

  const renderMessage = (message: Message) => {
    if (message.isTyping) {
      return (
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div className="bg-[#1a1a2e] rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%]">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-gray-400">Düşünüyorum...</span>
            </div>
          </div>
        </div>
      );
    }

    if (message.role === 'assistant') {
      // Tool aksiyon sonucu
      if (message.toolResult) {
        return (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl rounded-tl-sm ${
              message.toolResult.success
                ? 'bg-green-500/10 border border-green-500/20'
                : 'bg-red-500/10 border border-red-500/20'
            }`}>
              {message.toolResult.success
                ? <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
              <p className="text-sm text-gray-200">{message.toolResult.message}</p>
            </div>
          </div>
        );
      }

      // Tool aksiyon onay kartları
      if (message.toolCalls && message.toolCalls.length > 0) {
        const isExecuting = executingToolId === message.id;
        return (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 max-w-[90%] space-y-3">
              <p className="text-sm text-gray-400 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" />
                AI bir aksiyon öneriyor. Onaylamak istiyor musunuz?
              </p>
              {message.toolCalls.map((tool) => {
                const meta = TOOL_LABELS[tool.name];
                const Icon = meta?.icon || Zap;
                const isDanger = meta?.danger ?? false;
                return (
                  <div
                    key={tool.id}
                    className={`p-4 rounded-xl border ${isDanger ? 'border-red-500/30 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-4 h-4 ${isDanger ? 'text-red-400' : 'text-amber-400'}`} />
                      <span className={`text-sm font-semibold ${isDanger ? 'text-red-400' : 'text-amber-400'}`}>
                        {meta?.label || tool.name}
                      </span>
                    </div>
                    {/* Argümanlar */}
                    <div className="space-y-1 mb-3">
                      {Object.entries(tool.arguments).map(([k, v]) => (
                        <div key={k} className="flex items-center gap-2 text-xs">
                          <span className="text-gray-500 capitalize">{k}:</span>
                          <span className="text-gray-200 font-medium">{String(v)}</span>
                        </div>
                      ))}
                    </div>
                    {/* Onay butonları */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleExecuteTool(message.id, tool, true)}
                        disabled={isExecuting}
                        className="flex items-center gap-1.5 px-4 py-2 bg-green-500/20 border border-green-500/30 text-green-400 rounded-lg text-sm hover:bg-green-500/30 transition-colors disabled:opacity-50"
                      >
                        {isExecuting
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Onayla
                      </button>
                      <button
                        onClick={() => handleExecuteTool(message.id, tool, false)}
                        disabled={isExecuting}
                        className="flex items-center gap-1.5 px-4 py-2 bg-white/5 border border-white/10 text-gray-400 rounded-lg text-sm hover:bg-white/10 transition-colors disabled:opacity-50"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Reddet
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      // Normal metin mesajı
      return (
        <div className="flex items-start gap-3 group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 max-w-[85%]">
            <div className="bg-[#1a1a2e] rounded-2xl rounded-tl-sm px-4 py-3">
              <div
                className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap"
                dangerouslySetInnerHTML={{
                  __html: message.content
                    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                    .replace(/\n/g, '<br/>')
                }}
              />
            </div>
            <div className="flex items-center gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => handleCopy(message.id, message.content)}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                {copiedId === message.id ? (
                  <>
                    <Check className="w-3 h-3 text-green-400" />
                    <span className="text-green-400">Kopyalandı</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    <span>Kopyala</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-3 justify-end">
        <div className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
          <p className="text-gray-200 text-sm">{message.content}</p>
        </div>
        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-purple-400" />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] min-h-[500px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              AI Asistan
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                Aktif
              </span>
            </h2>
            <p className="text-sm text-gray-400">E-ticaret danışmanınız</p>
          </div>
        </div>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Yeni Sohbet
        </button>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {QUICK_ACTIONS.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.label}
              onClick={() => handleSuggestedQuestion(`${action.label} hakkında bilgi ver`)}
              className={`flex items-center gap-2 px-3 py-2 bg-${action.color}-500/10 border border-${action.color}-500/30 rounded-lg text-sm text-${action.color}-400 hover:bg-${action.color}-500/20 transition-colors whitespace-nowrap`}
            >
              <Icon className="w-4 h-4" />
              {action.label}
            </button>
          );
        })}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
        {messages.map((message) => (
          <div key={message.id}>
            {renderMessage(message)}
          </div>
        ))}

        {/* Suggested Questions - Only show after greeting */}
        {messages.length === 1 && !isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
            {SUGGESTED_QUESTIONS.map((question, index) => {
              const Icon = question.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestion(question.text, { includePlatformData: question.includePlatformData })}
                  className={`flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-${question.color}-500/50 rounded-xl text-left transition-all group`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-${question.color}-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-4 h-4 text-${question.color}-400`} />
                  </div>
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                    {question.text}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-500 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              );
            })}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="relative">
        <div className="absolute -top-12 left-0 right-0 h-12 bg-gradient-to-t from-[#0a0a12] to-transparent pointer-events-none" />
        <div className="relative flex items-end gap-2 p-3 bg-[#1a1a2e] rounded-2xl border border-white/10">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Sorunuzu yazın..."
            rows={1}
            className="flex-1 bg-transparent text-white placeholder-gray-500 resize-none focus:outline-none text-sm max-h-32"
            style={{ minHeight: '24px' }}
          />
          <button
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || isLoading}
            className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all ${
              inputValue.trim() && !isLoading
                ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:opacity-90'
                : 'bg-white/10 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-center text-xs text-gray-500 mt-2">
          AI yanıtları profil bilgilerinize göre özelleştirilir. Daha iyi öneriler için profilinizi tamamlayın.
        </p>
      </div>
    </div>
  );
};

export default AIChatPage;
